//************************************************************************ /
// CARD FUNDRAISER GOAL
// Displays a financial goal and its corresponding progress bar on
// a Campaign Card.
//************************************************************************ /

import React, { useState } from "react";
import { Popover } from "antd";
import {
  Progress,
  Divider,
  Group,
  Typography,
} from "@dreambigger/design-system/src/components";
import { InfoCircleOutlined } from "@ant-design/icons";
import cotributeDefaults from "../../../../assets/cotribute-defaults";
import {
  Community,
  EventFundraiser,
  User,
} from "@dreambigger/shared/src/types";
import { optimizeCloudinaryImg } from "../../../../utils";
export interface CardFundraisingGoalProps {
  community: Community;
  campaign: EventFundraiser;
  user?: User;
  font?: string;
}

const { Text } = Typography;

const CardFundraisingGoal = ({
  campaign,
  community,
  user,
  font = "proxima-nova",
}: CardFundraisingGoalProps) => {
  const getAmountMatched = () => {
    if (
      !campaign ||
      !campaign.totalDonated ||
      !campaign.totalDonatedFromDonors
    ) {
      return 0;
    }
    const amountMatched =
      parseInt(campaign?.totalDonated) -
      parseInt(campaign?.totalDonatedFromDonors);
    return amountMatched;
  };
  // DEFINE AMOUNTS RAISED AND
  // * The amountRaised is the amount imported from the DB, or 0 if blank.
  const amountRaised = campaign.totalDonatedFromDonors
    ? parseInt(campaign.totalDonatedFromDonors)
    : 0;
  // * The amountMatched is the total donations - donations from donors, or 0 by default.
  const amountMatched = getAmountMatched();

  const barColor = community?.meta?.brand?.colors?.primary;
  const goal = parseInt(campaign.goal);
  const donorCount = campaign.totalDonors || 0;
  const sponsorLogo = community?.meta?.brand?.logo || "";
  const raisedPercent = (amountRaised / goal) * 100;
  const matchPercent = amountMatched > 0 ? (amountMatched / goal) * 100 : null;
  const donationsFeesCoverage =
    community?.organization?.meta?.externalCampaign?.donationFeesCoverage;
  const donationLogo =
    community?.organization?.meta?.externalCampaign?.donationFeesCoverageLogo ||
    null;

  // Check if the "matched by" part of this component should be shown.
  const showMatch = !!campaign.matchFundsRemaining || !!donationsFeesCoverage;

  // If there is a match, determine the kind of logo to be displayed
  const matchLogo = donationLogo || sponsorLogo || null;

  // If there is a matchPercent, combine that with the raisedPercent. Otherwise, just use raisedPercent
  const combinedPercent = matchPercent
    ? raisedPercent + matchPercent
    : raisedPercent;

  const [poClicked, setPoClicked] = useState(false);
  const [poHovered, setPoHovered] = useState(false);

  const handleHoverChange = (visible: boolean) => {
    setPoHovered(visible);
    setPoClicked(false);
  };

  const handleClickChange = (visible: boolean) => {
    setPoHovered(visible);
    setPoClicked(!poClicked); // Toggle open and closed.
  };

  // The popover content that displays when mouse hovers over progress bar
  const renderPopover = (amountRaised: number, amountMatched: number) => {
    return (
      <>
        <div className="flex">
          {/* AMOUNT RAISED */}
          <div
            style={{
              width: "12px",
              height: "12px",
              border: "solid black 1px",
              backgroundColor: `${barColor || cotributeDefaults.primaryColor}`,
              alignSelf: "center",
              marginRight: "4px",
            }}
          />
          <p className="mb-0">{`Donations: $${amountRaised.toLocaleString()}`}</p>
        </div>
        {/* MATCH REMAINING (hidden if undefined or if it === 0*/}
        {amountMatched > 0 && (
          <div className="flex">
            <div
              style={{
                width: "12px",
                height: "12px",
                border: "solid black 1px",
                backgroundColor: "blue",
                alignSelf: "center",
                marginRight: "4px",
              }}
            />
            <p className="mb-0">{`Matched Funds: $${amountMatched.toLocaleString()}`}</p>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Divider />
      <Group spacing={3} className="ph-5 pv-4">
        {/* DONATION DETAILS */}
        <Group>
          {/* Amount Raised */}
          <Text className="fwb" style={{ fontFamily: font }}>
            ${(amountRaised + amountMatched).toLocaleString()}
          </Text>
          <Group xs="horizontal" spacing={3} className="justify-between">
            {/* Goal */}
            <Text className="f-2" style={{ fontFamily: font }}>
              of ${goal.toLocaleString()} goal
            </Text>
            {/* Raised by X Donors */}
            <Text className="f-2" style={{ fontFamily: font }}>
              raised by {donorCount} donors
            </Text>
          </Group>
        </Group>

        {/* PROGRESS BAR */}
        {/* Top <Popover> displays on hover (normal functionality). */}
        {/* Second <Popover> displays on click, which only matters for mobile use.*/}
        <Popover
          placement="bottomLeft"
          title={<span className="fwb">Goal Progress</span>}
          content={renderPopover(amountRaised, amountMatched)}
          mouseEnterDelay={0}
          mouseLeaveDelay={0}
          trigger="hover"
          open={poHovered}
          onOpenChange={handleHoverChange}
        >
          <Popover
            placement="bottomLeft"
            title={<span className="fwb">Goal Progress</span>}
            content={renderPopover(amountRaised, amountMatched)}
            trigger="click"
            open={poClicked}
            onOpenChange={handleClickChange}
          >
            {/* Div provides color for children to inherit */}
            <div className="primary">
              <Group spacing={2} xs="horizontal" style={{ cursor: "pointer" }}>
                <InfoCircleOutlined className="self-center" />
                <Progress
                  success={{
                    strokeColor: barColor || cotributeDefaults.primaryColor,
                    percent: raisedPercent,
                  }}
                  percent={combinedPercent}
                  showInfo={false}
                  strokeColor={"blue"}
                  trailColor="#E8E8E8"
                />
              </Group>
            </div>
          </Popover>
        </Popover>

        {/* DDONATIONS MATCHED BY (Optional) */}
        {showMatch && matchLogo && (
          <div className="flex items-center justify-around">
            <p
              className="f-1 mb-0 w-50 gray-7"
              style={{ fontFamily: font, lineHeight: "12px" }}
            >
              {matchPercent
                ? "Donations matched by"
                : "Donation fees covered by"}
            </p>
            {/* Sponsor Logo */}

            <div className="w-50 pl-3">
              <img
                src={optimizeCloudinaryImg(matchLogo)}
                alt="Sponsor Logo"
                className="w-100"
                style={{ objectFit: "contain" }}
              />
            </div>
          </div>
        )}
      </Group>
    </>
  );
};

export default CardFundraisingGoal;
